/**
 * Portable Firestore constraint translation — deliberately free of any Firebase SDK import.
 *
 * Two SDKs consume this: the JS `firebase/firestore` container (web) and
 * `@react-native-firebase/firestore` (mobile, ELARA-981). It lives in its own module rather than
 * beside {@link createFirestoreContainer} for the same reason `firebase/analytics` is kept out of
 * shared code: importing the JS-SDK container would pull `firebase/firestore` into the React Native
 * bundle purely to reach this pure function — which also breaks under the mobile jest transform.
 *
 * Only the builders differ per SDK; the ordering rules below are identical and worth sharing.
 */
import type { ConstraintSpec } from '@elara/firebolt/shared';

import { groupBy, last, mapValues, partition } from 'lodash-es';

/**
 * The constraint builders a Firestore SDK must supply. Both the JS SDK and
 * `@react-native-firebase/firestore` expose this exact set.
 */
export interface ConstraintBuilders<T> {
  where(field: string, operator: never, value: never): T;
  orderBy(field: string, direction?: never): T;
  limit(count: number): T;
  limitToLast(count: number): T;
  startAt(...values: unknown[]): T;
  startAfter(...values: unknown[]): T;
  endBefore(...values: unknown[]): T;
  endAt(...values: unknown[]): T;
}

function translateConstraintSpec<T>(constraint: ConstraintSpec, build: ConstraintBuilders<T>): T {
  switch (constraint.type) {
    case 'where': {
      return build.where(constraint.field, constraint.operator as never, constraint.value as never);
    }
    case 'orderBy': {
      return build.orderBy(constraint.field, constraint.direction as never);
    }
    case 'limit': {
      return build.limit(constraint.limit);
    }
    case 'limitToLast': {
      return build.limitToLast(constraint.limitToLast);
    }
    case 'startAt': {
      return build.startAt(...constraint.values);
    }
    case 'startAfter': {
      return build.startAfter(...constraint.values);
    }
    case 'endBefore': {
      return build.endBefore(...constraint.values);
    }
    case 'endAt': {
      return build.endAt(...constraint.values);
    }
    default: {
      throw new Error(`Unknown constraint type: ${String(constraint)}`);
    }
  }
}

/**
 * Translates portable {@link ConstraintSpec}s into a concrete SDK's constraints.
 *
 * The de-duplication matters and is why this is shared rather than reimplemented per platform: when a
 * spec carries several `orderBy`s for the SAME field only the last wins, and every `orderBy` must
 * precede the other constraints.
 */
export function translateConstraintSpecsWith<T>(constraints: ConstraintSpec[], build: ConstraintBuilders<T>): T[] {
  const [orderByConstraints, otherConstraints] = partition(constraints, (constraint) => constraint.type === 'orderBy');
  const orderByGroupedByField = groupBy(orderByConstraints, (c) => c.field);
  const latestOrderByByField = mapValues(orderByGroupedByField, (cs) => last(cs));
  const prioritizedOrderByConstraints = Object.values(latestOrderByByField).filter((c) => c !== undefined);
  return [...prioritizedOrderByConstraints, ...otherConstraints]
    .map((c) => translateConstraintSpec(c, build))
    .filter((c) => c !== undefined);
}
